export interface MarketplaceFilters {
  categories: string[]
  locations: string[]
  minBudgetEUR: number | null
  maxBudgetEUR: number | null
  moveIn: 'any' | 'immediate' | '3months' | 'specific'
  moveInSpecificMonth: string | null
  configuration: string[]
  // Reverse-marketplace semantics: landlords only need "no smokers" / "no pets"
  // gates. "Accepts" everything is the natural default.
  noSmokers: boolean
  noPets: boolean
  petTypes: string[]
  duration: string[]
  verifiedOnly: boolean
  incomeVerifiedOnly: boolean
  minRating: number
  sort: string
}

export const DEFAULT_FILTERS: MarketplaceFilters = {
  categories: [],
  locations: [],
  minBudgetEUR: null,
  maxBudgetEUR: null,
  moveIn: 'any',
  moveInSpecificMonth: null,
  configuration: [],
  noSmokers: false,
  noPets: false,
  petTypes: [],
  duration: [],
  verifiedOnly: false,
  incomeVerifiedOnly: false,
  minRating: 0,
  sort: 'newest',
}

export function countActiveFilters(filters: MarketplaceFilters): number {
  let count = 0
  if (filters.configuration.length > 0) count++
  if (filters.noSmokers) count++
  if (filters.noPets) count++
  if (filters.duration.length > 0) count++
  if (filters.verifiedOnly) count++
  if (filters.incomeVerifiedOnly) count++
  if (filters.minRating > 0) count++
  return count
}

export function filtersFromProperty(
  property: any,
): Partial<MarketplaceFilters> {
  const filters: Partial<MarketplaceFilters> = {}

  if (property.priceEur) {
    filters.minBudgetEUR = property.priceEur
  } else if (property.price) {
    // Assuming RON if no priceEur, rough conversion
    filters.minBudgetEUR = Math.round(property.price / 5)
  }

  if (property.city) {
    // We would need to map property.neighborhood to the full location string
    // For now, just set it if we can find it
    // This is a heuristic
  }

  if (property.type) {
    if (property.type === 'apartment' && property.rooms) {
      filters.categories = [`apartament_bloc|${property.rooms} cam`]
    } else if (property.type === 'house') {
      filters.categories = ['casa_vila']
    } else if (property.type === 'studio') {
      filters.categories = ['garsoniera']
    }
  }

  // Pet-friendly property: do NOT auto-apply the "no pets" filter — leave it off.
  // (Previous heuristic was misaligned now that the field is a hard gate.)

  return filters
}

export function computeMatchScore(
  profile: any,
  filters: MarketplaceFilters,
): number {
  let budgetFit = 0
  if (filters.minBudgetEUR) {
    if (profile.budgetMax >= filters.minBudgetEUR) {
      budgetFit = 1
    } else {
      budgetFit = Math.max(0, profile.budgetMax / filters.minBudgetEUR)
    }
  } else {
    budgetFit = 1
  }

  const verificationLevel =
    ((profile.verifications?.email ? 1 : 0) +
      (profile.verifications?.identity ? 1 : 0) +
      (profile.verifications?.income ? 1 : 0)) /
    3

  let urgencyFit = 0.5 // Default
  if (profile.moveInDate) {
    // Simple heuristic: if it contains current or next month, higher score
    const currentMonth = new Date()
      .toLocaleString('ro-RO', { month: 'long' })
      .toLowerCase()
    if (profile.moveInDate.toLowerCase().includes(currentMonth)) {
      urgencyFit = 1
    }
  }

  const ratingFit = (profile.rating || 0) / 5

  return (
    0.4 * budgetFit +
    0.3 * verificationLevel +
    0.2 * urgencyFit +
    0.1 * ratingFit
  )
}

export function filterProfiles(
  profiles: any[],
  filters: MarketplaceFilters,
): any[] {
  return profiles.filter((p) => {
    // 1. Categories
    if (filters.categories.length > 0) {
      const matchesCategory = filters.categories.some((cat) => {
        const [type, rooms] = cat.split('|')
        if (rooms) {
          return p.housingType === type && p.rooms === rooms
        }
        return p.housingType === type
      })
      if (!matchesCategory) return false
    }

    // 2. Locations
    if (filters.locations.length > 0) {
      const matchesLocation = filters.locations.some((loc) => {
        const parts = loc.split('|')
        const city = parts[0]
        const zone = parts[parts.length - 1] // Last part is always the zone or city

        if (parts.length === 1) {
          return p.city === city
        }

        // If it's a specific zone
        return p.city === city && p.neighborhoods?.includes(zone)
      })
      if (!matchesLocation) return false
    }

    // 3. Budget
    if (filters.minBudgetEUR && p.budgetMax < filters.minBudgetEUR) return false
    if (filters.maxBudgetEUR && p.budgetMin > filters.maxBudgetEUR) return false

    // 4. Move In
    if (filters.moveIn === 'immediate') {
      // Heuristic: check if moveInDate is current month
      const currentMonth = new Date()
        .toLocaleString('ro-RO', { month: 'long' })
        .toLowerCase()
      if (!p.moveInDate?.toLowerCase().includes(currentMonth)) return false
    } else if (filters.moveIn === 'specific' && filters.moveInSpecificMonth) {
      if (
        !p.moveInDate
          ?.toLowerCase()
          .includes(filters.moveInSpecificMonth.toLowerCase())
      )
        return false
    }

    // 5. Configuration
    if (filters.configuration.length > 0) {
      const isCombo = p.comboMembers && p.comboMembers.length > 1
      const config = isCombo
        ? 'combo'
        : p.coupleStatus === 'cuplu'
          ? 'cuplu'
          : 'singur'
      if (!filters.configuration.includes(config)) return false
    }

    // 6. Lifestyle gates — only exclude when the landlord has set a hard "no".
    if (filters.noSmokers && p.isSmoker) return false
    if (filters.noPets && p.hasPets) return false

    // 7. Duration
    if (filters.duration.length > 0) {
      let durCat = '1y'
      if (p.contractDuration?.includes('lun')) durCat = 'short'
      else if (p.contractDuration?.includes('2 ani')) durCat = '2y'

      if (!filters.duration.includes(durCat)) return false
    }

    // 8. Verifications
    if (
      filters.verifiedOnly &&
      (!p.verifications?.email || !p.verifications?.identity)
    )
      return false
    if (filters.incomeVerifiedOnly && !p.verifications?.income) return false

    // 9. Rating
    if (filters.minRating > 0 && (p.rating || 0) < filters.minRating)
      return false

    return true
  })
}

export function sortProfiles(
  profiles: any[],
  sortKey: string,
  filters: MarketplaceFilters,
): any[] {
  const sorted = [...profiles]

  switch (sortKey) {
    case 'match':
      sorted.sort(
        (a, b) => computeMatchScore(b, filters) - computeMatchScore(a, filters),
      )
      break
    case 'urgency':
      // Simple heuristic: sort by moveInDate string alphabetically (not perfect but works for mock)
      sorted.sort((a, b) =>
        (a.moveInDate || '').localeCompare(b.moveInDate || ''),
      )
      break
    case 'budget_desc':
      sorted.sort((a, b) => (b.budgetMax || 0) - (a.budgetMax || 0))
      break
    case 'newest':
    default:
      // Mock profiles are already in a reasonable order, just reverse to simulate newest
      // Or leave as is if we want stable sort
      break
  }

  return sorted
}
